/**
 * chatPolicy - role-based access control for AI chat capabilities.
 *
 * Defines what capabilities are available to a user based on their project role.
 * The policy is computed at the ProjectPage level and passed down to AiChatPane.
 *
 * Current desired behavior (see context/10-product/roles.md):
 *   - admin     → full access (no restriction)
 *   - manager   → full access (no restriction)
 *   - viewer    → full access (no restriction)
 *   - collaborator → restricted: summary-oriented capabilities only
 *
 * Collaborator restriction details (current default policy):
 *   - Only 'ask' mode is available; no agent tool execution
 *   - Cannot initiate complex multi-step agent runs
 *
 * Future extension point:
 *   - Project admins/managers will be able to configure allowed skill keys for
 *     the collaborator role via a project-settings API.
 *   - getProjectChatPolicy() should accept an optional ProjectPolicyOverride from
 *     the API and merge it with the default policy below.
 *   - See TODO in getProjectChatPolicy() for the recommended extension hook.
 *
 * See context/10-product/roles.md for role definitions.
 * See context/50-ai/06-ai-chat.md for AI chat architecture.
 */

/** Project-level roles as stored in project_user table */
export type ProjectRole = 'admin' | 'manager' | 'viewer' | 'collaborator';

/**
 * ChatPolicy defines what AI chat capabilities are available to the current user.
 *
 * - `role`          - the user's resolved project role (or 'unknown' if not yet loaded)
 * - `isRestricted`  - true when the user is in a reduced-capability tier
 * - `allowedModes`  - which chat modes (auto/ask/agent) the user may select
 *
 * Extension point: In a future iteration `customAllowedSkillKeys` will be
 * populated from a project-level policy record so admins/managers can expand
 * collaborator access without a code change.
 */
export interface ChatPolicy {
  role: ProjectRole | 'unknown';
  isRestricted: boolean;
  allowedModes: Array<'auto' | 'ask' | 'agent'>;
  /**
   * TODO (future): project-admin-configured list of skill keys allowed for
   * collaborator. Empty array means the default policy applies (isRestricted).
   */
  customAllowedSkillKeys?: string[];
}

/** Full-access policy - for admin, manager, and viewer roles. */
export const FULL_ACCESS_POLICY: ChatPolicy = {
  role: 'admin',
  isRestricted: false,
  allowedModes: ['auto', 'ask', 'agent'],
};

/**
 * Restricted policy - default for the collaborator role.
 * Only 'ask' mode is available; no agent multi-step tool execution.
 * This is the baseline until admin customization is wired up.
 */
export const COLLABORATOR_POLICY: ChatPolicy = {
  role: 'collaborator',
  isRestricted: true,
  allowedModes: ['ask'],
};

/**
 * Returns the appropriate ChatPolicy for a given project role.
 *
 * TODO (future): accept an optional `projectPolicyOverride` param sourced from
 * `/api/project/:uuid/chat-policy` so admins/managers can expand collaborator
 * access without a code deploy. Shape:
 *   { collaboratorAllowedSkillKeys: string[]; collaboratorAllowedModes: ChatMode[] }
 */
export function getProjectChatPolicy(role: ProjectRole | null | undefined): ChatPolicy {
  switch (role) {
    case 'admin':
      return { ...FULL_ACCESS_POLICY, role: 'admin' };
    case 'manager':
      return { ...FULL_ACCESS_POLICY, role: 'manager' };
    case 'viewer':
      return { ...FULL_ACCESS_POLICY, role: 'viewer' };
    case 'collaborator':
      return COLLABORATOR_POLICY;
    default:
      // Role unknown (data not yet loaded or user has no explicit project role).
      // Default to full access; the backend always enforces the authoritative policy.
      return { ...FULL_ACCESS_POLICY, role: 'unknown' };
  }
}
